import WishlistToggleButton from '@/components/creator-discovery/WishlistToggleButton';
import CreatorAvatar from '@/components/creators/CreatorAvatar';
import { Tooltip } from '@/components/ui/tooltip';
import { Box, HStack, Span, VStack } from '@chakra-ui/react';
import { useAuth } from '@/contexts/auth-context';
import { useContactSummaryQuery } from '@/hooks/queries/use-contact-summary-query';
import { Mail } from 'iconoir-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isIdentityLocked, isStatsLocked } from '@/components/plan-locks/creator-locks';
import { UnlockedMarker } from '@/components/plan-locks/UnlockedMarker';
import type { ProfileUnlockTarget } from '@/hooks/actions/use-unlock-creator-profile-action';
import type { ContactResolveRef } from '@/hooks/queries/use-resolve-contact-query';
import ContactPrimaryButton from '../ContactPrimaryButton';
import type { ContactPlatform } from '../contact-drawer-url-state';
import type { ContactShell } from '../contact-types';
import { useActiveContactProfile } from '../profile/use-active-contact-profile';
import { useContactResolveRef } from '../use-contact-resolve-ref';
import { useResolvedContactIdentityId } from '../use-resolved-contact-identity-id';
import { contactRelationOf } from './contact-relation';
import ContactRelationBadge from './ContactRelationBadge';
import { ContactUnlockSlot } from './ContactUnlockSlot';
import { useContactLocation } from './use-contact-location';
import { useContactWishlistStar } from './use-contact-wishlist-star';

/**
 * The creator a profile unlock names: a masked prospect by its dataset
 * reference, a creator opened by handle by its reference when the entry
 * carries one, else by handle. Affiliates and identities are never prospects.
 */
const unlockTargetOf = (ref: ContactResolveRef | null): ProfileUnlockTarget | null => {
  if (ref?.entryType === 'source') return { sourceCreatorId: ref.id };
  if (ref?.entryType === 'creator') {
    return ref.sourceCreatorId ? { sourceCreatorId: ref.sourceCreatorId } : { username: ref.id };
  }
  return null;
};

interface ContactIdentityHeaderProps {
  contact: ContactShell;
  /** The platform the drawer shows, whose live profile picture the avatar reads. */
  platform: ContactPlatform;
  /** Opens the email outreach view. Fired only for the email channel. */
  onInvite?: () => void;
}

/**
 * Identity header at the top of the rail: a centred column with the avatar, the
 * name, a location line (only when the profile says where the creator lives)
 * and the relation badge (affiliate / prospect), then the action row: a primary
 * email CTA and a favorite (wishlist) star toggle. It scrolls with the rail. The CTA is
 * email-only (a cold Instagram DM is forbidden); when the contact has no email
 * the button is disabled and a tooltip explains why. On a prospect the plan
 * masks, while the free plan counts profile unlocks, the unlock button takes
 * its place, with the month's counter under it.
 */
const ContactIdentityHeader: React.FC<ContactIdentityHeaderProps> = ({
  contact,
  platform,
  onInvite,
}) => {
  const { t } = useTranslation();
  const { canToggle, isInWishlist, toggle, isBusy } = useContactWishlistStar(contact);
  const resolveRef = useContactResolveRef();
  const unlockTarget = unlockTargetOf(resolveRef);
  const refKey = resolveRef ? `${resolveRef.entryType}:${resolveRef.id}` : null;
  // Set by a successful unlock of this very creator, so the drawer reads it
  // unlocked before the refetched contact says so.
  const [unlocked, setUnlocked] = useState<{ refKey: string; outcome: string } | null>(null);
  const justUnlocked = refKey !== null && unlocked?.refKey === refKey;
  // A relation or the plan already showing the creator is no unlock: no marker.
  const marked = contact.unlocked || (justUnlocked && unlocked?.outcome !== 'already_visible');
  const isMasked = (isIdentityLocked(contact) || isStatsLocked(contact)) && !justUnlocked;
  const offersUnlock = isMasked && unlockTarget !== null;
  // The brand mailbox cannot write to a prospect whose address the plan
  // withholds; proposing a collaboration and Fluenceur messaging stay open.
  const emailLocked = isIdentityLocked(contact) && !justUnlocked;
  const emailDisabled = contact.primary.disabled || emailLocked;
  // The same picture as the profile header: the live profile's, hosted by us,
  // before the one the entry resolved with. An affiliate without an uploaded
  // photo or an identity entry resolves with none, and a creator entry with a
  // mirrored Instagram link that expires; both left initials beside the photo.
  // The live profile is the drawer's cached profile query, never a new request.
  const liveProfile = useActiveContactProfile(contact, platform);
  const location = useContactLocation(contact, platform);
  // The relation reads the summary's collaboration rows; until the summary has
  // answered they are empty, which would flash "Prospect" on an affiliate.
  const { user } = useAuth();
  const identityId = useResolvedContactIdentityId();
  const { data: summary } = useContactSummaryQuery(user?.currentBrandId, identityId);
  const relation = summary ? contactRelationOf(contact.collaborations) : null;
  const avatarUrl = liveProfile?.profilePictureUrl || contact.avatarUrl || undefined;
  const avatarCreatorId =
    resolveRef?.entryType === 'creator' && resolveRef.platform === 'instagram'
      ? resolveRef.id
      : undefined;

  const handlePrimaryClick = () => {
    // Email is the only header channel. When there is no email the button is
    // disabled (deriveContactPrimaryAction), so this never fires for a contact
    // the brand cannot reach.
    if (emailDisabled) return;
    onInvite?.();
  };

  // Tooltip only when the CTA is disabled (no reachable email); otherwise the
  // tooltip is inert and the button renders on its own.
  const emailButton = (
    <Tooltip
      content={emailLocked ? t('planLocks.tooltip') : t('contactDrawer.actions.noEmail')}
      disabled={!emailDisabled}
    >
      <ContactPrimaryButton
        flex="1"
        h="38px"
        rounded="lg"
        fontSize="12.5px"
        fontWeight="medium"
        disabled={emailDisabled}
        onClick={handlePrimaryClick}
      >
        <Mail width={16} height={16} />
        {t(contact.primary.labelKey)}
      </ContactPrimaryButton>
    </Tooltip>
  );

  return (
    <>
      <VStack gap="10px" align="center" textAlign="center" px="18px" pt="24px" pb="16px">
        <CreatorAvatar
          creatorId={avatarCreatorId}
          creatorName={contact.name}
          fallbackSrc={avatarUrl}
          lazyFetch={Boolean(avatarUrl && avatarCreatorId)}
          flex="none"
          boxSize="64px"
          colorPalette="primary"
        />
        <VStack gap="2px" align="center" maxW="full" minW="0">
          <Span
            maxW="full"
            fontFamily="heading"
            fontSize="17px"
            fontWeight="semibold"
            letterSpacing="-0.015em"
            color="ink"
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
          >
            {contact.name}
          </Span>
          {location && (
            <Span fontFamily="body" fontSize="12.5px" color="primary.500">
              {location}
            </Span>
          )}
          <UnlockedMarker creator={{ unlocked: marked }} />
        </VStack>
        {relation && (
          <HStack gap="6px" wrap="wrap" justify="center">
            <ContactRelationBadge relation={relation} />
          </HStack>
        )}
      </VStack>

      <HStack gap="2" px="18px">
        {/* A masked prospect on a plan that counts unlocks: the unlock button
            takes the email button's place until the profile is open. */}
        {offersUnlock && unlockTarget ? (
          <ContactUnlockSlot
            target={unlockTarget}
            fallback={emailButton}
            onUnlocked={(outcome) => refKey && setUnlocked({ refKey, outcome })}
          />
        ) : (
          emailButton
        )}
        {/* Reuses the discovery WishlistToggleButton (star + outline + a11y label),
            backed by real wishlist membership via useContactWishlistStar. Disabled
            (inert) when the contact has no keyable creator id, so it never shows a
            false empty star for an affiliate / un-keyed identity. */}
        <WishlistToggleButton
          isInWishlist={isInWishlist}
          aria-pressed={isInWishlist}
          disabled={!canToggle || isBusy}
          onClick={toggle}
          flex="none"
          boxSize="38px"
          {...(!isInWishlist && { borderColor: 'border.emphasized', bg: 'panelBg' })}
        />
      </HStack>
      <Box srOnly aria-live="polite">
        {justUnlocked ? t('planLocks.unlock.done') : ''}
      </Box>
    </>
  );
};

export default ContactIdentityHeader;
